import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import {
	type CalendarEvent,
	KIND_META,
	marketingHubApi,
} from "@/lib/marketing/hub-api";

const MOIS = [
	"janvier",
	"février",
	"mars",
	"avril",
	"mai",
	"juin",
	"juillet",
	"août",
	"septembre",
	"octobre",
	"novembre",
	"décembre",
];

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const moisCourant = () => new Date().toISOString().slice(0, 7);

/** Décale un mois « AAAA-MM » de n mois. */
const decale = (mois: string, n: number): string => {
	const [a, m] = mois.split("-").map(Number);
	const d = new Date(Date.UTC(a, m - 1 + n, 1));
	return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

/**
 * Cases du mois, alignées sur une semaine commençant le lundi.
 *
 * `getUTCDay()` rend 0 pour dimanche : on le ramène en fin de semaine, sinon
 * la grille décalerait d'un jour toute l'année.
 */
function cases(mois: string): Array<number | null> {
	const [a, m] = mois.split("-").map(Number);
	const premier = new Date(Date.UTC(a, m - 1, 1));
	const nbJours = new Date(Date.UTC(a, m, 0)).getUTCDate();
	const decalage = (premier.getUTCDay() + 6) % 7;

	const grille: Array<number | null> = Array.from({ length: decalage }, () => null);
	for (let j = 1; j <= nbJours; j += 1) grille.push(j);
	while (grille.length % 7 !== 0) grille.push(null);
	return grille;
}

/**
 * Calendrier éditorial : tout ce qui part, tous canaux confondus.
 *
 * C'est le seul écran qui rend visible une collision — une newsletter et un
 * push le même matin, deux bannières qui se succèdent sans transition. Avant,
 * chaque canal avait sa liste et personne ne voyait l'ensemble.
 */
export function AdminMarketingCalendrierPage() {
	const [mois, setMois] = useState(moisCourant);
	const [vue, setVue] = useState<"mois" | "liste">("mois");

	const { data, isLoading } = useQuery({
		queryKey: ["marketing", "calendar", mois],
		queryFn: () => marketingHubApi.calendar(mois),
	});

	const [annee, numMois] = mois.split("-").map(Number);
	const aujourdhui = new Date();
	const estMoisCourant = mois === moisCourant();

	// Les événements rangés par jour : la grille n'a plus qu'à lire.
	const parJour = new Map<number, CalendarEvent[]>();
	for (const e of data?.events ?? []) {
		const j = new Date(e.at).getUTCDate();
		parJour.set(j, [...(parJour.get(j) ?? []), e]);
	}

	return (
		<>
			<PageHead
				title="Calendrier éditorial"
				sub="Bannières, newsletters, notifications, WhatsApp et articles"
			/>

			<Card>
				<CardHead
					title={`${MOIS[numMois - 1]} ${annee}`}
					sub={
						isLoading
							? "Chargement…"
							: `${data?.events.length ?? 0} publication(s) programmée(s)`
					}
					right={
						<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
							<Segmented
								value={vue}
								onChange={setVue}
								options={[
									{ value: "mois", label: "Mois" },
									{ value: "liste", label: "Liste" },
								]}
							/>
							<AdminButton
								variant="ghost"
								size="sm"
								iconOnly
								aria-label="Mois précédent"
								title="Mois précédent"
								onClick={() => setMois((m) => decale(m, -1))}
							>
								<AdminIcon n="chevL" s={14} />
							</AdminButton>
							<AdminButton
								variant="ghost"
								size="sm"
								onClick={() => setMois(moisCourant())}
							>
								Aujourd'hui
							</AdminButton>
							<AdminButton
								variant="ghost"
								size="sm"
								iconOnly
								aria-label="Mois suivant"
								title="Mois suivant"
								onClick={() => setMois((m) => decale(m, 1))}
							>
								<AdminIcon n="chevR" s={14} />
							</AdminButton>
						</div>
					}
				/>
				<CardBody>
					{vue === "mois" ? (
						<>
							<div className="cal-grid" style={{ marginBottom: 4 }}>
								{JOURS.map((j) => (
									<div
										key={j}
										className="muted"
										style={{
											fontSize: 10,
											fontWeight: 700,
											textTransform: "uppercase",
											textAlign: "center",
											padding: "2px 0",
										}}
									>
										{j}
									</div>
								))}
							</div>
							<div className="cal-grid">
								{cases(mois).map((j, i) => {
									const evenements = j ? (parJour.get(j) ?? []) : [];
									const estAujourdhui =
										estMoisCourant && j === aujourdhui.getDate();
									return (
										<div
											key={i}
											className={`cal-cell${j === null ? " muted" : ""}${
												estAujourdhui ? " today" : ""
											}`}
										>
											{j !== null && (
												<>
													<div className="d">{j}</div>
													{evenements.map((e) => {
														const meta = KIND_META[e.kind];
														return (
															<Link
																key={`${e.kind}-${e.id}`}
																to={meta.to}
																className={`cal-event ${meta.tone}`}
																style={{
																	display: "block",
																	textDecoration: "none",
																}}
																title={`${meta.label} · ${e.title}`}
															>
																{e.title}
															</Link>
														);
													})}
												</>
											)}
										</div>
									);
								})}
							</div>
						</>
					) : (data?.events.length ?? 0) === 0 ? (
						<div className="empty">Rien de programmé ce mois-ci.</div>
					) : (
						<div>
							{(data?.events ?? []).map((e, i, arr) => {
								const meta = KIND_META[e.kind];
								return (
									<Link
										key={`${e.kind}-${e.id}`}
										to={meta.to}
										style={{
											display: "flex",
											alignItems: "center",
											gap: 12,
											padding: "10px 0",
											borderBottom:
												i < arr.length - 1 ? "1px solid var(--a-border)" : "none",
											color: "inherit",
											textDecoration: "none",
										}}
									>
										<span
											className="tn muted"
											style={{ width: 86, fontSize: 11, flexShrink: 0 }}
										>
											{new Date(e.at).toLocaleString("fr-FR", {
												day: "2-digit",
												month: "2-digit",
												hour: "2-digit",
												minute: "2-digit",
											})}
										</span>
										<span className={`cal-event ${meta.tone}`}>{meta.label}</span>
										<strong style={{ flex: 1, fontSize: 12.5, minWidth: 0 }}>
											{e.title}
										</strong>
										<Tag variant="neutral" noDot>
											{e.status}
										</Tag>
										<AdminIcon n="chevR" s={14} c="var(--a-ink-faint)" />
									</Link>
								);
							})}
						</div>
					)}
				</CardBody>
			</Card>

			<p
				className="muted"
				style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.6 }}
			>
				Les publications se créent depuis l'écran de leur canal : une bannière a
				une période et une cible, une newsletter a une audience et un contenu —
				les programmer depuis une case de calendrier obligerait à redemander
				tout cela ici.
			</p>
		</>
	);
}
